package com.vidaensupalabra.vsp.ventanas

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

import com.vidaensupalabra.vsp.ui.theme.VspBase
import com.vidaensupalabra.vsp.ui.theme.VspMarco
import com.vidaensupalabra.vsp.ui.theme.VspMarcoTransparente50
import com.vidaensupalabra.vsp.ui.theme.White

private const val CONFESION_FE_URL = "https://vidaensupalabra.com/wp-content/uploads/2023/08/Confesion-de-Fe-Bautista-de-Londres-de-1689-Guia-de-estudio.pdf"

// Se cambia DrawableResource por un String que representa el archivo en la carpeta public web
private data class InfoItem(val title: String, val content: String, val iconSrc: String? = null)

private val HITOS_LIST = listOf(
    "En 2013, inicia nuestra comunidad en un estudio bíblico en casa.",
    "En 2014, escogimos el nombre de nuestra comunidad y establecimos nuestra membresía.",
    "En 2015, se ordenaron nuestros pastores de manera oficial por la membresía.",
    "En 2017, recibimos nuestra personería jurídica como iglesia cristiana ante el Estado.",
    "En 2018, nos vinculamos a ACTS 29 para ser parte de esta red de plantación de Iglesias.",
    "Actualmente, nuestro deseo es seguir trabajando para la gloria de Dios."
)

private val MISSION_VISION_LIST = listOf(
    InfoItem("Nuestra Misión", "Hacer crecer la iglesia de Cristo en nuestra ciudad, proclamando y enseñando Su palabra, para traer esperanza y transformación de vida a nuestras familias.", "drawable/mision_vsp.svg"),
    InfoItem("Nuestra Visión", "Ser conocidos en nuestra ciudad como una iglesia que sirve como Jesús lo hace, fundamentados en Sus palabras, interesados en las personas y sus necesidades, y con un compromiso total por la gloria de Dios y Su reino.", "drawable/flag_vsp.svg")
)

private val BELIEFS_LIST = listOf(
    InfoItem("DE LAS ESCRITURAS", "Creemos que la Biblia es la Palabra de Dios, compuesta por 39 libros en el Antiguo Testamento y 27 en el Nuevo Testamento, que fue escrita por hombres divinamente inspirados y que es un tesoro perfecto de instrucción celestial. Creemos que la Biblia tiene a Dios mismo por autor, por objeto la salvación y por contenido la verdad sin mezcla alguna de error..."),
    InfoItem("DE DIOS", "Creemos que hay un solo Dios viviente y verdadero, infinito, Espíritu inteligente, Creador y Gobernador supremo del cielo y de la tierra, indeciblemente glorioso en santidad; merecedor de toda la honra, confianza y amor..."),
    InfoItem("DEL SER HUMANO", "Creemos que los seres humanos fueron creados, tanto el hombre como la mujer, a la imagen de Dios, igualmente dignos, en santidad y sujeción a la ley de su Creador..."),
    InfoItem("DE LA SALVACIÓN", "Creemos que la salvación del ser humano es completamente por gracia, por fe en la obra redentora de Jesucristo, el Hijo de Dios..."),
    InfoItem("DE LA IGLESIA", "Creemos que la Iglesia es la comunidad de todos los creyentes en el evangelio de Jesucristo de todas las épocas, en todo el mundo, visiblemente establecida en iglesias locales..."),
    InfoItem("DE LOS ÚLTIMOS TIEMPOS", "Creemos que Cristo vuelve a juzgar a los vivos y a los muertos. Hasta su regreso, los creyentes deben vivir sus vidas de tal manera que den gloria a Dios a través de Jesucristo..."),
    InfoItem("DE LA FAMILIA", "Creemos que Dios creó la familia para ser la primera y fundamental institución humana, en donde a partir de las relaciones personales entre sus miembros, se crea el ambiente ideal para el desarrollo del amor mutuo...")
)

@Composable
fun InformationScreen() {
    Div(attrs = {
        style {
            width(100.percent)
            minHeight(100.vh)
            backgroundColor(VspBase)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            fontFamily("sans-serif")
        }
    }) {
        Div(attrs = {
            style {
                width(100.percent)
                maxWidth(800.px) // Restringe en pantallas anchas
                padding(16.px)
                boxSizing("border-box")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
            }
        }) {
            AboutUsSection()
            Div(attrs = { style { height(16.px) } })
            MissionVisionSection()
            Div(attrs = { style { height(16.px) } })
            MilestonesSection()
            Div(attrs = { style { height(16.px) } })
            BeliefsSection()
            Div(attrs = { style { height(32.px) } })
            ConfessionOfFaithButton()
            Div(attrs = { style { height(40.px) } }) // Margen final inferior
        }
    }
}

@Composable
private fun AboutUsSection() {
    H2(attrs = {
        style {
            color(White)
            fontWeight("bold")
            fontSize(24.px)
            margin(0.px, 0.px, 16.px, 0.px)
        }
    }) { Text("NUESTRA IGLESIA") }
    
    ImageWithFullscreenPreview(
        src = "drawable/vspsomos.jpg",
        altText = "Foto de la iglesia"
    )

    // Reemplazo de buildAnnotatedString por etiquetas HTML nativas (B = Negrita, U = Subrayado)
    P(attrs = {
        style {
            color(White)
            textAlign("center")
            fontSize(16.px)
            lineHeight("1.5")
            margin(16.px, 0.px, 0.px, 0.px)
        }
    }) {
        Text("Somos una comunidad de discípulos de Cristo que buscan glorificar a Dios, ")
        B { Span(attrs = { style { property("text-decoration", "underline") } }) { Text("viviendo") } }
        Text(" centrados en el evangelio, ")
        B { Span(attrs = { style { property("text-decoration", "underline") } }) { Text("proclamando") } }
        Text(" fieles Su palabra y ")
        B { Span(attrs = { style { property("text-decoration", "underline") } }) { Text("dependiendo") } }
        Text(" en todo del poder de Su Espíritu.")
    }
}

@Composable
private fun MissionVisionSection() {
    var selectedIndex by remember { mutableStateOf<Int?>(null) }
    
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            width(100.percent)
            justifyContent(JustifyContent.SpaceEvenly)
            flexWrap(FlexWrap.Wrap)
            gap(16.px)
        }
    }) {
        MISSION_VISION_LIST.forEachIndexed { index, item ->
            ExpandableTab(
                iconSrc = item.iconSrc ?: "",
                title = item.title,
                isExpanded = selectedIndex == index,
                onExpand = { selectedIndex = if (selectedIndex == index) null else index }
            )
        }
    }
    
    selectedIndex?.let { index ->
        ExpandedContentCard(
            title = MISSION_VISION_LIST[index].title,
            content = MISSION_VISION_LIST[index].content
        )
    }
}

@Composable
private fun MilestonesSection() {
    Div(attrs = { style { height(24.px) } })
    
    H3(attrs = {
        style { color(White); fontWeight("bold"); fontSize(20.px); margin(0.px, 0.px, 16.px, 0.px); width(100.percent) }
    }) { Text("HITOS") }

    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px); width(100.percent) } }) {
        HITOS_LIST.forEach { hito ->
            Div(attrs = {
                style { display(DisplayStyle.Flex); alignItems(AlignItems.FlexStart) }
            }) {
                Img(src = "drawable/ic_tiempo.svg", alt = "Tiempo", attrs = {
                    style { width(24.px); height(24.px); marginRight(12.px); property("filter", "invert(62%) sepia(91%) saturate(347%) hue-rotate(133deg) brightness(85%) contrast(85%)") } // Aproximación CSS a tint VspMarco
                })
                Span(attrs = {
                    style { color(White); fontSize(16.px); lineHeight("1.5") }
                }) { Text(hito) }
            }
        }
    }
}

@Composable
private fun BeliefsSection() {
    Div(attrs = { style { height(24.px) } })
    
    H3(attrs = {
        style { color(White); fontWeight("bold"); fontSize(20.px); margin(0.px, 0.px, 16.px, 0.px); width(100.percent) }
    }) { Text("LO QUE CREEMOS") }
    
    P(attrs = {
        style { color(White); fontSize(16.px); lineHeight("1.5"); marginBottom(16.px); width(100.percent) }
    }) {
        Text("Somos una iglesia cristiana de confesión Bautista Reformada, por lo que reconocemos en el documento de la Confesión de Londres de 1689, un trabajo que resume nuestros fundamentos doctrinales de fe y práctica para todo lo somos y lo que hacemos como iglesia.")
        Br()
        Br()
        Text("Dentro de los principales distintivos doctrinales que nos identifican, podemos resumirlos de la siguiente manera:")
    }
    
    Div(attrs = { style { width(100.percent); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px) } }) {
        BELIEFS_LIST.forEach { belief ->
            ExpandableBeliefCard(title = belief.title, description = belief.content)
        }
    }
}

@Composable
private fun ExpandableBeliefCard(title: String, description: String) {
    var isExpanded by remember { mutableStateOf(false) }

    Div(attrs = {
        style {
            width(100.percent)
            backgroundColor(VspMarcoTransparente50)
            borderRadius(8.px)
            boxSizing("border-box")
            property("overflow", "hidden")
        }
    }) {
        // Encabezado clickeable
        Div(attrs = {
            onClick { isExpanded = !isExpanded }
            style {
                padding(16.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                cursor("pointer")
            }
        }) {
            Span(attrs = {
                style { color(VspMarco); fontSize(20.px); marginRight(12.px); fontWeight("bold") }
            }) { Text(if (isExpanded) "▼" else "+") }
            
            Span(attrs = {
                style { color(White); fontSize(16.px); fontWeight("bold") }
            }) { Text(title) }
        }
        
        // Contenido desplegable (usando display none/block para la animación visual simple en web)
        Div(attrs = {
            style {
                display(if (isExpanded) DisplayStyle.Block else DisplayStyle.None)
                padding(0.px, 16.px, 16.px, 16.px)
            }
        }) {
            P(attrs = {
                style { color(White); fontSize(14.px); margin(0.px); lineHeight("1.5") }
            }) { Text(description) }
        }
    }
}

@Composable
private fun ExpandableTab(iconSrc: String, title: String, isExpanded: Boolean, onExpand: () -> Unit) {
    Div(attrs = {
        onClick { onExpand() }
        style {
            padding(8.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            cursor("pointer")
        }
    }) {
        Img(src = iconSrc, alt = title, attrs = {
            style {
                width(48.px)
                height(48.px)
                marginBottom(8.px)
                // Filtro CSS para colorear imágenes transparentes, equivalente a tint = VspMarco
                property("filter", "invert(62%) sepia(91%) saturate(347%) hue-rotate(133deg) brightness(85%) contrast(85%)")
            }
        })

        Div(attrs = {
            style { display(DisplayStyle.Flex); alignItems(AlignItems.Center) }
        }) {
            Span(attrs = { style { color(VspMarco); fontSize(18.px); marginRight(4.px) } }) {
                Text(if (isExpanded) "▼" else "➕") // Usando caracteres como iconos
            }
            Span(attrs = { style { color(VspMarco); fontSize(16.px); fontWeight("bold") } }) {
                Text(title)
            }
        }
    }
}

@Composable
private fun ExpandedContentCard(title: String, content: String) {
    Div(attrs = {
        style {
            width(100.percent)
            marginTop(16.px)
            padding(16.px)
            backgroundColor(VspMarcoTransparente50)
            borderRadius(8.px)
            property("box-shadow", "0px 4px 6px rgba(0,0,0,0.1)")
            boxSizing("border-box")
        }
    }) {
        H3(attrs = {
            style { color(VspMarco); fontSize(18.px); textAlign("center"); margin(0.px, 0.px, 12.px, 0.px) }
        }) { Text(title) }
        
        P(attrs = {
            style { color(White); fontSize(16.px); textAlign("center"); margin(0.px); lineHeight("1.4") }
        }) { Text(content) }
    }
}

@Composable
private fun ConfessionOfFaithButton() {
    Button(attrs = {
        onClick { window.open(CONFESION_FE_URL, "_blank") }
        style {
            width(100.percent)
            padding(16.px)
            backgroundColor(VspMarcoTransparente50)
            color(White)
            fontSize(16.px)
            fontWeight("bold")
            border(0.px)
            borderRadius(8.px)
            cursor("pointer")
        }
    }) { Text("Descarga la CFBL 1689") }
}

@Composable
private fun ImageWithFullscreenPreview(src: String, altText: String) {
    var showDialog by remember { mutableStateOf(false) }

    // Imagen en miniatura
    Img(
        src = src,
        alt = altText,
        attrs = {
            onClick { showDialog = true }
            style {
                width(100.percent)
                maxHeight(300.px)
                property("object-fit", "cover")
                borderRadius(8.px)
                cursor("zoom-in")
            }
        }
    )

    // Modal de pantalla completa
    if (showDialog) {
        Div(attrs = {
            onClick { showDialog = false }
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.vw)
                height(100.vh)
                backgroundColor(rgba(0, 0, 0, 0.9))
                property("z-index", 1000)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                cursor("zoom-out")
            }
        }) {
            // Se confía en el zoom nativo del navegador (o simplemente se muestra grande contenida)
            Img(
                src = src,
                alt = "$altText (Ampliada)",
                attrs = {
                    style {
                        maxWidth(95.percent)
                        maxHeight(95.percent)
                        property("object-fit", "contain")
                    }
                }
            )
        }
    }
}